import { useState } from 'react';
import { Loader2, X, Users } from 'lucide-react';
import { removeAgencyContact } from '@generated/server/findContacts';
import { sendLetterToAll } from '@generated/server/letters';
import { getStaffPass } from '@generated/hooks/staffPass';
import { SendBadges } from '@generated/components/SendBadges';
import type { LettersData } from '@generated/components/LettersPanel';

/** The extra people on an agency's email list, plus a button to send a letter to everyone at the agency. */
export function AgencyPeople({ agency, data, onChanged, onNotice }: {
  agency: { id: string; name: string; email: { email: string } | null };
  data: LettersData | null; onChanged: () => void; onNotice: (m: string) => void;
}) {
  const people = (data?.contacts ?? []).filter((c) => c.agencyId === agency.id);
  const letters = data?.letters ?? [];
  const [num, setNum] = useState(1);
  const [busy, setBusy] = useState('');
  const [armed, setArmed] = useState(false);
  const total = (agency.email?.email ? 1 : 0) + people.length;
  if (!people.length) return null;

  async function remove(id: string) {
    setBusy(id);
    try { await removeAgencyContact({ data: { pass: getStaffPass(), contactId: id } }); onChanged(); }
    catch (e) { console.error(e); onNotice('Could not remove that person.'); } finally { setBusy(''); }
  }
  async function sendAll() {
    if (!armed) { setArmed(true); return; }
    setArmed(false);
    setBusy('send');
    try {
      const r = await sendLetterToAll({ data: { pass: getStaffPass(), letterNumber: num, agencyId: agency.id } });
      onNotice(`Letter ${num} sent to ${r.sent} ${r.sent === 1 ? 'person' : 'people'} at ${agency.name}${r.skipped ? ` (${r.skipped} skipped)` : ''}${r.failed ? `, ${r.failed} failed` : ''}.`);
      onChanged();
    } catch (e) { console.error(e); onNotice(e instanceof Error && e.message.startsWith('CREDITS:') ? e.message.slice(9) : 'Sending did not go through. Please try again.'); }
    finally { setBusy(''); }
  }

  return (
    <div className="mt-2 border-l-4 border-foreground/30 pl-3">
      <p className="text-xs font-bold uppercase tracking-wide">Also on the email list</p>
      <ul className="mt-1 space-y-1">
        {people.map((p) => (
          <li key={p.id} className="flex items-center gap-2 text-sm">
            <span className="min-w-0 break-words">
              {p.name || p.title}{p.name && p.title && <span className="text-muted-foreground"> · {p.title}</span>} · {p.email}
              {p.emailKind === 'pattern guess' && <span className="ml-1 text-[10px] font-semibold uppercase text-destructive">guessed</span>}
              <span className="ml-1"><SendBadges sends={data?.sends ?? []} email={p.email} /></span>
            </span>
            <button onClick={() => remove(p.id)} disabled={!!busy} aria-label={`Remove ${p.name}`} className="inline-flex h-8 w-8 shrink-0 items-center justify-center hover:bg-muted">
              {busy === p.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />}
            </button>
          </li>
        ))}
      </ul>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <select value={num} onChange={(e) => setNum(Number(e.target.value))} aria-label="Which letter" className="min-h-10 border-2 border-foreground bg-background px-2 text-sm">
          {letters.map((l) => <option key={l.id} value={l.number}>Letter {l.number}</option>)}
        </select>
        <button onClick={sendAll} disabled={!!busy} className="inline-flex min-h-10 items-center gap-2 border-2 border-foreground bg-[hsl(var(--brand-yellow))] px-3 text-sm font-semibold disabled:opacity-50">
          {busy === 'send' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Users className="h-4 w-4" />}{armed ? `Click again to send to all ${total}` : `Send to all ${total} here`}
        </button>
      </div>
    </div>
  );
}
